"use client";

import React from "react";
import { ArrowRight, Leaf, Sparkles, Sprout, Zap } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const categories = [{
  id: "fmcg",
  title: "FMCG",
  count: "142",
  description: "Fast-moving consumer goods, food & beverage, and daily essentials.",
  span: "md:col-span-2 md:row-span-2",
  bgClass: "bg-sidebar text-sidebar-foreground",
  patternType: "fluid"
}, {
  id: "wellness",
  title: "Natural Wellness",
  count: "86",
  description: "Ayurveda, organic supplements, and holistic health brands.",
  span: "md:col-span-1 md:row-span-2",
  bgClass: "bg-secondary text-secondary-foreground",
  patternType: "leaf"
}, {
  id: "sustainability",
  title: "Sustainability",
  count: "54",
  description: "Eco-friendly, biodegradable, and green tech trademarks.",
  span: "md:col-span-1 md:row-span-1",
  bgClass: "bg-sidebar text-sidebar-foreground",
  patternType: "circles"
}, {
  id: "agri",
  title: "Agriculture",
  count: "73",
  description: "Agri-tech, farming equipment, and organic produce.",
  span: "md:col-span-1 md:row-span-1",
  bgClass: "bg-secondary text-secondary-foreground",
  patternType: "agri"
}, {
  id: "d2c",
  title: "D2C Brands",
  count: "118",
  description: "Direct-to-consumer apparel, lifestyle, and personal care.",
  span: "md:col-span-2 md:row-span-1",
  bgClass: "bg-sidebar text-sidebar-foreground",
  patternType: "d2c"
}];
const PatternFluid = () => <div className="absolute inset-0 overflow-hidden opacity-20 pointer-events-none">
    <div className="absolute -top-[20%] -left-[10%] w-[70%] h-[70%] rounded-full bg-primary blur-[80px] mix-blend-screen" />
    <div className="absolute -bottom-[20%] -right-[10%] w-[60%] h-[60%] rounded-full bg-accent blur-[80px] mix-blend-screen" />
  </div>;
const PatternLeaf = () => <div className="absolute inset-0 overflow-hidden flex items-center justify-center opacity-15 pointer-events-none">
    <Leaf className="w-[150%] h-[150%] text-primary stroke-[0.5] -rotate-12 translate-x-10 translate-y-10" />
  </div>;
const PatternCircles = () => <div className="absolute inset-0 overflow-hidden flex items-center justify-center opacity-15 pointer-events-none">
    <div className="w-[200%] aspect-square rounded-full border border-primary absolute" />
    <div className="w-[150%] aspect-square rounded-full border border-primary absolute" />
    <div className="w-[100%] aspect-square rounded-full border border-primary absolute" />
    <div className="w-[50%] aspect-square rounded-full border border-primary absolute" />
  </div>;
const PatternAgri = () => <div className="absolute inset-0 overflow-hidden flex items-end justify-start opacity-10 pointer-events-none p-6">
    <Sprout className="w-48 h-48 text-primary stroke-[0.5] -translate-x-10 translate-y-10" />
  </div>;
const PatternD2C = () => <div className="absolute inset-0 overflow-hidden opacity-10 pointer-events-none">
    <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_25%,rgba(255,215,0,0.2)_50%,transparent_75%,transparent_100%)] bg-[length:20px_20px]" />
    <Zap className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 text-primary stroke-[0.5]" />
  </div>;
export default function PremiumAssetsPreview() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="premium-assets-preview" className="py-24 bg-background text-foreground overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        
        {}
        <div ref={headerRef} className={`flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 transition-all duration-1000 ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
              <Sparkles className="w-4 h-4" />
              <span>Curated Portfolio</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-4 text-foreground">
              The Golden Standard of <br className="hidden md:block" />
              <span className="text-primary">Brand Assets</span>
            </h2>
            <p className="text-muted-foreground text-lg">
              Explore our multi-class, legally verified trademarks ready for immediate acquisition and launch.
            </p>
          </div>
          <div className="shrink-0">
            <Button asChild size="lg" className="group">
              <Link to="/OurCataloguePage">
                View Full Catalogue
                <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
          </div>
        </div>

        {}
        <div ref={gridRef} className={`grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 auto-rows-[280px] gap-4 md:gap-6 transition-all duration-1000 delay-200 ${isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {categories.map((category, i) => <Card key={category.id} data-index={i} className={`group relative overflow-hidden border-2 border-transparent hover:border-primary transition-all duration-500 flex flex-col justify-between p-6 md:p-8 ${category.span} ${category.bgClass}`}>
              {}
              {category.patternType === "fluid" && <PatternFluid />}
              {category.patternType === "leaf" && <PatternLeaf />}
              {category.patternType === "circles" && <PatternCircles />}
              {category.patternType === "agri" && <PatternAgri />}
              {category.patternType === "d2c" && <PatternD2C />}

              {}
              <div className="relative z-10 flex justify-between items-start">
                <div className="inline-flex items-center justify-center px-3 py-1 rounded-full bg-background/10 backdrop-blur-sm border border-white/10 text-sm font-medium">
                  Class {i + 1 * 3} {}
                </div>
                <div className="w-10 h-10 rounded-full bg-background/10 backdrop-blur-sm border border-white/10 flex items-center justify-center opacity-0 -translate-y-4 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
                  <ArrowRight className="w-5 h-5 text-primary" />
                </div>
              </div>

              <div className="relative z-10 mt-auto">
                <div className="text-4xl md:text-5xl font-bold text-primary mb-2 tracking-tighter">
                  {category.count}
                </div>
                <h3 className="text-xl md:text-2xl font-semibold mb-2">
                  {category.title}
                </h3>
                <p className="text-sm opacity-80 max-w-[80%] line-clamp-2">
                  {category.description}
                </p>
              </div>
              
              {}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
            </Card>)}
        </div>

      </div>
    </section>;
}